/**
 * Configuration loader for ludicrous-gateway
 * Loads and validates configuration from YAML files
 */

import * as fs from "node:fs";
import * as path from "node:path";
import yaml from "js-yaml";
import { GatewayConfigSchema, type ValidatedGatewayConfig } from "./schema.js";
import { getDefaultConfig } from "./default.js";
import type { GatewayConfig } from "../types/config.js";
import { getLogger } from "../utils/logger.js";

/**
 * Configuration loading options
 */
export interface ConfigLoadOptions {
  /** Path to config file */
  configPath?: string;
  /** Whether to use defaults if no config found */
  useDefaults?: boolean;
}

/**
 * Expand environment variables in a string
 * Supports ${VAR_NAME} syntax
 */
function expandEnvVars(value: string): string {
  return value.replace(/\$\{([^}]+)\}/g, (_, envVar: string) => {
    const envValue = process.env[envVar];
    if (envValue === undefined) {
      getLogger().warn(`Environment variable ${envVar} not set`);
      return "";
    }
    return envValue;
  });
}

/**
 * Recursively expand environment variables in config object
 */
function expandConfigEnvVars(obj: unknown): unknown {
  if (typeof obj === "string") {
    return expandEnvVars(obj);
  }

  if (Array.isArray(obj)) {
    return obj.map(expandConfigEnvVars);
  }

  if (obj !== null && typeof obj === "object") {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
      result[key] = expandConfigEnvVars(value);
    }
    return result;
  }

  return obj;
}

/**
 * Default config file search paths
 */
const CONFIG_SEARCH_PATHS = [
  "./config/local.yaml",
  "./config/config.yaml",
  "./ludicrous-gateway.yaml",
  "~/.config/ludicrous-gateway/config.yaml",
];

/**
 * Find config file in default locations
 */
function findConfigFile(): string | null {
  for (const searchPath of CONFIG_SEARCH_PATHS) {
    const resolvedPath = searchPath.startsWith("~")
      ? path.join(process.env["HOME"] ?? "", searchPath.slice(1))
      : path.resolve(searchPath);

    if (fs.existsSync(resolvedPath)) {
      return resolvedPath;
    }
  }
  return null;
}

/**
 * Load and parse YAML config file
 */
function loadYamlFile(filePath: string): unknown {
  const content = fs.readFileSync(filePath, "utf-8");
  return yaml.load(content);
}

/**
 * Merge configurations with defaults
 */
function mergeWithDefaults(
  config: Partial<GatewayConfig>
): Partial<GatewayConfig> {
  const defaults = getDefaultConfig();

  return {
    server: { ...defaults.server, ...config.server },
    routing: {
      ...defaults.routing,
      ...config.routing,
      rules: config.routing?.rules ?? defaults.routing.rules,
    },
    providers: config.providers ?? defaults.providers,
    health: {
      ...defaults.health,
      ...config.health,
      circuit_breaker: {
        ...defaults.health.circuit_breaker,
        ...config.health?.circuit_breaker,
      },
    },
    logging: { ...defaults.logging, ...config.logging },
  };
}

/**
 * Load configuration from file or defaults
 */
export function loadConfig(options: ConfigLoadOptions = {}): GatewayConfig {
  const logger = getLogger();
  const { configPath, useDefaults = true } = options;

  // Determine config file path
  let filePath = configPath;
  if (!filePath) {
    filePath = findConfigFile() ?? undefined;
  }

  // Load raw config
  let rawConfig: unknown;

  if (filePath && fs.existsSync(filePath)) {
    logger.info(`Loading config from ${filePath}`);
    rawConfig = loadYamlFile(filePath);
  } else if (useDefaults) {
    logger.info("No config file found, using defaults");
    rawConfig = {};
  } else {
    throw new Error(
      `Config file not found: ${filePath ?? "no path specified"}`
    );
  }

  // Expand environment variables
  const expandedConfig = expandConfigEnvVars(rawConfig);

  // Merge with defaults
  const mergedConfig = mergeWithDefaults(
    expandedConfig as Partial<GatewayConfig>
  );

  // Validate with Zod
  const parseResult = GatewayConfigSchema.safeParse(mergedConfig);

  if (!parseResult.success) {
    const errors = parseResult.error.errors
      .map((e) => `${e.path.join(".")}: ${e.message}`)
      .join("\n");
    throw new Error(`Invalid configuration:\n${errors}`);
  }

  return parseResult.data as GatewayConfig;
}

/**
 * Validate a configuration without loading
 */
export function validateConfig(
  config: unknown
): { valid: true; config: ValidatedGatewayConfig } | { valid: false; errors: string[] } {
  const parseResult = GatewayConfigSchema.safeParse(config);

  if (parseResult.success) {
    return { valid: true, config: parseResult.data };
  }

  return {
    valid: false,
    errors: parseResult.error.errors.map(
      (e) => `${e.path.join(".")}: ${e.message}`
    ),
  };
}

// Re-export schema and defaults
export { GatewayConfigSchema } from "./schema.js";
export { getDefaultConfig } from "./default.js";
